import React, { useEffect, useState, useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Table,
  Button,
  ButtonGroup,
  Label,
  UncontrolledTooltip
} from 'reactstrap';
import BZAPIService from '../../../services/BZAPIService';
import Select from 'react-select';
import Loader from '../../common/Loader';
import * as echarts from 'echarts';
import ReactEcharts from 'echarts-for-react';
import AppContext from '../../../context/Context';
import { getPosition } from '../../../helpers/utils';
import { chartTheme, chartTooltip } from '../../../helpers/chartTheme';
import moment from 'moment';

const DAYS_RANGE_OPTIONS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'All time', value: 0 }
];

const EXECUTION_COUNT_OPTIONS = [
  { label: 'Last 10', value: 10 },
  { label: 'Last 25', value: 25 },
  { label: 'Last 50', value: 50 }
];

const VIEW_MODES = {
  AGGREGATED: 'aggregated',
  EXECUTIONS: 'executions',
  TRENDS: 'trends'
};

const selectStyles = {
  option: (styles, { isFocused, isSelected }) => {
    return {
      ...styles,
      color: isFocused || isSelected ? 'white' : 'var(--ink)'
    };
  }
};

const selectTheme = theme => ({
  ...theme,
  borderRadius: 0,
  colors: {
    ...theme.colors,
    primary25: '#253746',
    primary: '#bd472a'
  }
});

// Stat Card Component
const StatCard = ({ label, value, unit }) => (
  <Card className="bg-light border mb-3">
    <CardBody className="p-3 text-center">
      <h3 className="fs-2 font-weight-normal text-dark">{value?.toLocaleString() ?? '-'}</h3>
      <p className="mb-0 text-800">
        {label}
        {unit && <span className="text-muted"> ({unit})</span>}
      </p>
    </CardBody>
  </Card>
);

StatCard.propTypes = {
  label: PropTypes.string,
  value: PropTypes.number,
  unit: PropTypes.string
};

// Trend Chart Component
const TrendChart = ({ data, catalog, selectedStats, isDark }) => {
  const t = chartTheme(isDark);

  const colors = ['#2c7be5', '#00d97e', '#e63757', '#f5803e', '#6b5eae', '#27bcfd'];

  const series = selectedStats.map((statKey, index) => {
    const catalogItem = catalog.find(c => c.key === statKey);
    return {
      name: catalogItem?.label || statKey,
      type: 'line',
      data: data.map(d => d[statKey] || 0),
      smooth: true,
      lineStyle: { width: 2 },
      itemStyle: { color: colors[index % colors.length] }
    };
  });

  const option = {
    tooltip: chartTooltip(t, {
      trigger: 'axis',
      padding: [7, 10],
      position(pos, params, dom, rect, size) {
        return getPosition(pos, params, dom, rect, size);
      }
    }),
    legend: {
      data: selectedStats.map(key => catalog.find(c => c.key === key)?.label || key),
      bottom: 0,
      textStyle: { color: t.inkMuted }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: data.map(d => moment(d.statDate).format('MMM D')),
      axisLine: { lineStyle: { color: t.line } },
      axisLabel: { color: t.inkMuted }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: t.lineSoft } },
      axisLabel: { color: t.inkMuted }
    },
    series
  };

  return <ReactEcharts echarts={echarts} option={option} style={{ height: '300px', width: '100%' }} />;
};

TrendChart.propTypes = {
  data: PropTypes.array,
  catalog: PropTypes.array,
  selectedStats: PropTypes.array,
  isDark: PropTypes.bool
};

export default function ExecutionStatsTab({ vendorId }) {
  const { isDark } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [executionData, setExecutionData] = useState(null);
  const [viewMode, setViewMode] = useState(VIEW_MODES.AGGREGATED);
  const [daysRange, setDaysRange] = useState(DAYS_RANGE_OPTIONS[1]); // Default: Last 30 days
  const [executionCount, setExecutionCount] = useState(EXECUTION_COUNT_OPTIONS[0]); // Default: Last 10
  const [selectedTrendStats, setSelectedTrendStats] = useState(['totalBugsInserted', 'totalBugsUpdated']);

  const fetchExecutionHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const options = {
        aggregatedDaysRange: daysRange.value
      };

      if (viewMode === VIEW_MODES.EXECUTIONS) {
        options.count = executionCount.value;
      }

      if (viewMode === VIEW_MODES.TRENDS) {
        options.lastDays = daysRange.value === 0 ? 365 : daysRange.value;
      }

      const data = await BZAPIService.getExecutionHistory(vendorId, options);
      setExecutionData(data);
    } catch (e) {
      console.error('Failed to fetch execution history:', e);
      setError('Failed to load execution history. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [vendorId, daysRange, executionCount, viewMode]);

  useEffect(() => {
    fetchExecutionHistory();
  }, [fetchExecutionHistory]);

  // Group catalog by group field
  const groupedCatalog = useMemo(() => {
    if (!executionData?.catalog) return {};
    return executionData.catalog.reduce((acc, item) => {
      const group = item.group || 'Other';
      if (!acc[group]) acc[group] = [];
      acc[group].push(item);
      return acc;
    }, {});
  }, [executionData?.catalog]);

  // Get stats that have data for trend selection
  const availableTrendStats = useMemo(() => {
    if (!executionData?.catalog) return [];
    return executionData.catalog.filter(c => c.type === 'int').map(c => ({ label: c.label, value: c.key }));
  }, [executionData?.catalog]);

  const handleRefresh = () => {
    fetchExecutionHistory();
  };

  const renderAggregatedView = () => {
    const { aggregated } = executionData || {};

    if (!aggregated || Object.keys(aggregated).length === 0) {
      return (
        <div className="text-center py-4">
          <p className="text-muted">No stats available for the selected time range.</p>
        </div>
      );
    }

    return (
      <>
        {Object.entries(groupedCatalog).map(([groupName, items]) => (
          <div key={groupName} className="mb-4">
            <h6 className="text-uppercase text-muted mb-3">{groupName}</h6>
            <Row>
              {items.map(item => (
                <Col lg="3" md="4" sm="6" key={item.key}>
                  <StatCard label={item.label} value={aggregated[item.key]} unit={item.unit} />
                </Col>
              ))}
            </Row>
          </div>
        ))}
      </>
    );
  };

  const renderExecutionsView = () => {
    const { executions, catalog } = executionData || {};

    if (!executions || executions.length === 0) {
      return (
        <div className="text-center py-4">
          <p className="text-muted">No execution history available. Run the bug service to generate data.</p>
        </div>
      );
    }

    // Get visible columns from catalog
    const visibleColumns = catalog?.filter(c => c.type === 'int').slice(0, 6) || [];

    return (
      <div className="table-responsive">
        <Table bordered striped className="fs--1">
          <thead className="card-header">
            <tr>
              <th>Started</th>
              <th>Ended</th>
              <th>Runtime</th>
              {visibleColumns.map(col => (
                <th key={col.key}>{col.label}</th>
              ))}
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {executions.map((exec, index) => (
              <tr key={`exec-${index}`}>
                <td>{exec.startedAt ? moment(exec.startedAt).format('MMM D, h:mm A') : '-'}</td>
                <td>{exec.endedAt ? moment(exec.endedAt).format('MMM D, h:mm A') : '-'}</td>
                <td>{exec.runTime ? `${Math.round(exec.runTime / 60)}m ${exec.runTime % 60}s` : '-'}</td>
                {visibleColumns.map(col => (
                  <td key={col.key} className="text-center">
                    {exec[col.key] !== undefined ? exec[col.key].toLocaleString() : '-'}
                  </td>
                ))}
                <td>
                  {exec.message && exec.message.length > 50 ? (
                    <>
                      <span id={`msg-${index}`}>{exec.message.substring(0, 50)}...</span>
                      <UncontrolledTooltip target={`msg-${index}`} placement="top">
                        {exec.message}
                      </UncontrolledTooltip>
                    </>
                  ) : (
                    exec.message || '-'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderTrendsView = () => {
    const { days, catalog } = executionData || {};

    if (!days || days.length === 0) {
      return (
        <div className="text-center py-4">
          <p className="text-muted">No daily statistics recorded yet.</p>
        </div>
      );
    }

    return (
      <>
        <Row className="mb-3">
          <Col sm="6">
            <Label>Select stats to display:</Label>
            <Select
              isMulti
              options={availableTrendStats}
              value={availableTrendStats.filter(s => selectedTrendStats.includes(s.value))}
              onChange={selected => setSelectedTrendStats(selected ? selected.map(s => s.value) : [])}
              menuPortalTarget={document.body}
              styles={selectStyles}
              theme={selectTheme}
              className="basic-multi-select"
            />
          </Col>
        </Row>
        {selectedTrendStats.length > 0 ? (
          <TrendChart data={days} catalog={catalog} selectedStats={selectedTrendStats} isDark={isDark} />
        ) : (
          <div className="text-center py-4">
            <p className="text-muted">Select at least one stat to display the trend chart.</p>
          </div>
        )}
      </>
    );
  };

  return (
    <Card className="mb-4">
      <CardBody className="fs--1">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <CardTitle className="mb-0">Bug Service Execution Stats</CardTitle>
          <Button color="falcon-default" size="sm" onClick={handleRefresh} disabled={isLoading}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-1"
            >
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
            Refresh
          </Button>
        </div>

        {/* View Mode Toggle */}
        <Row className="mb-4">
          <Col sm="auto">
            <ButtonGroup>
              <Button
                color={viewMode === VIEW_MODES.AGGREGATED ? 'primary' : 'falcon-default'}
                onClick={() => setViewMode(VIEW_MODES.AGGREGATED)}
              >
                Aggregated Stats
              </Button>
              <Button
                color={viewMode === VIEW_MODES.EXECUTIONS ? 'primary' : 'falcon-default'}
                onClick={() => setViewMode(VIEW_MODES.EXECUTIONS)}
              >
                Executions
              </Button>
              <Button
                color={viewMode === VIEW_MODES.TRENDS ? 'primary' : 'falcon-default'}
                onClick={() => setViewMode(VIEW_MODES.TRENDS)}
              >
                Trends
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        {/* Controls */}
        <Row className="mb-4">
          {(viewMode === VIEW_MODES.AGGREGATED || viewMode === VIEW_MODES.TRENDS) && (
            <Col sm="4">
              <Label>Time Range:</Label>
              <Select
                options={DAYS_RANGE_OPTIONS}
                value={daysRange}
                onChange={setDaysRange}
                menuPortalTarget={document.body}
                styles={selectStyles}
                theme={selectTheme}
                className="basic-single"
                isDisabled={isLoading}
              />
            </Col>
          )}
          {viewMode === VIEW_MODES.EXECUTIONS && (
            <Col sm="4">
              <Label>Number of executions:</Label>
              <Select
                options={EXECUTION_COUNT_OPTIONS}
                value={executionCount}
                onChange={setExecutionCount}
                menuPortalTarget={document.body}
                styles={selectStyles}
                theme={selectTheme}
                className="basic-single"
                isDisabled={isLoading}
              />
            </Col>
          )}
        </Row>

        {/* Content Area with Loading State */}
        {isLoading ? (
          <div className="py-4">
            <Loader text="Loading execution stats..." />
          </div>
        ) : error ? (
          <div className="text-center py-4">
            <p className="text-danger">{error}</p>
            <Button color="primary" size="sm" onClick={handleRefresh}>
              Try Again
            </Button>
          </div>
        ) : (
          <>
            {/* Summary Info */}
            {executionData && (
              <p className="text-muted mb-4">
                {viewMode === VIEW_MODES.AGGREGATED &&
                  `Showing aggregated stats for ${
                    daysRange.value === 0 ? 'all time' : `the last ${daysRange.value} days`
                  }`}
                {viewMode === VIEW_MODES.EXECUTIONS &&
                  `Showing ${executionData.executions?.length || 0} of ${executionCount.value} requested executions`}
                {viewMode === VIEW_MODES.TRENDS && `Showing daily trends for ${executionData.days?.length || 0} days`}
              </p>
            )}

            {/* Content Views */}
            {viewMode === VIEW_MODES.AGGREGATED && renderAggregatedView()}
            {viewMode === VIEW_MODES.EXECUTIONS && renderExecutionsView()}
            {viewMode === VIEW_MODES.TRENDS && renderTrendsView()}
          </>
        )}
      </CardBody>
    </Card>
  );
}

ExecutionStatsTab.propTypes = {
  vendorId: PropTypes.string.isRequired
};
